import { CustomDatePicker, CustomSelect } from '../common';

function SalesOrderForm({
  values,
  errors,
  customerOptions,
  customerBranchOptions,
  seaweedTypeOptions,
  containerOptions,
  selectedContainer,
  isSaving,
  canCreate,
  includeCustomerBranch = false,
  includeSeaweedType = false,
  includeCommercialFields = false,
  includeContainerDetails = true,
  includeWarehouseLocation = true,
  onChange,
  onSubmit,
  onCancel,
}) {
  return (
    <form
      className="rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] p-5 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.03)] sm:p-6"
      onSubmit={onSubmit}
    >
      <div className="mb-6 border-b border-[var(--theme-border-soft)] pb-5">
        <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
          Sales Order Module
        </p>
        <h2 className="m-0 mt-2 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
          Add Sales Order
        </h2>
        <p className="m-0 mt-2 text-[0.95rem] text-[var(--theme-text-muted)]">
          {getSalesOrderHeaderDescription({
            includeCustomerBranch,
            includeCommercialFields,
            includeContainerDetails,
          })}
        </p>
      </div>

      <section className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.025)] sm:p-5">
        <div className="mb-4">
          <h3 className="m-0 text-[1rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
            Order Details
          </h3>
          <p className="m-0 mt-2 text-[0.9rem] text-[var(--theme-text-muted)]">
            {getOrderDetailsDescription({
              includeCustomerBranch,
              includeContainerDetails,
              includeWarehouseLocation,
            })}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className={includeSeaweedType ? 'xl:col-start-1 xl:row-start-1' : ''}>
            <FormSelect
              label="Customer"
              name="customerId"
              value={values.customerId}
              error={errors.customerId}
              onChange={onChange}
              options={customerOptions}
              placeholder="Select customer"
            />
          </div>
          {includeCustomerBranch ? (
            <div className={includeSeaweedType ? 'xl:col-start-2 xl:row-start-1' : ''}>
              <FormSelect
                label="Delivery Location"
                name="branch"
                value={values.branch}
                error={errors.branch}
                onChange={onChange}
                options={customerBranchOptions}
                placeholder="Select delivery location"
                disabled={!values.customerId || !customerBranchOptions.length}
              />
            </div>
          ) : null}
          {includeSeaweedType ? (
            <div className="xl:col-start-3 xl:row-start-1">
              <FormSelect
                label="Seaweed Type"
                name="seaweedType"
                value={values.seaweedType}
                error={errors.seaweedType}
                onChange={onChange}
                options={seaweedTypeOptions}
                placeholder="Select seaweed type"
                disabled={!seaweedTypeOptions.length}
              />
            </div>
          ) : null}
          {includeCommercialFields ? (
            <div className={includeSeaweedType ? 'xl:col-start-1 xl:row-start-2' : ''}>
              <FormInput
                label="FCL Quantity"
                name="quantity"
                value={values.quantity}
                error={errors.quantity}
                onChange={onChange}
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter FCL quantity"
              />
            </div>
          ) : null}
          {includeContainerDetails ? (
            <>
              <FormInput
                label="FCL Batch Number"
                name="fclBatchNumber"
                value={values.fclBatchNumber}
                error={errors.fclBatchNumber}
                onChange={onChange}
                placeholder="e.g. 574"
              />
              <FormInput
                label="Code Number"
                name="codeNumber"
                value={values.codeNumber}
                error={errors.codeNumber}
                onChange={onChange}
                placeholder="e.g. MUK 854"
              />
              <FormInput
                label="Container Number"
                name="containerNumber"
                value={values.containerNumber}
                error={errors.containerNumber}
                onChange={onChange}
                placeholder="Enter container number"
              />
            </>
          ) : null}
          <div className={includeCommercialFields ? 'xl:col-start-2 xl:row-start-2' : ''}>
            <CustomDatePicker
              label="S.O. Date"
              name="soDate"
              value={values.soDate}
              error={errors.soDate}
              onChange={onChange}
            />
          </div>
          {includeWarehouseLocation ? (
            <FormInput
              label="Warehouse Location / Branch"
              name="warehouseLocation"
              value={values.warehouseLocation}
              error={errors.warehouseLocation}
              onChange={onChange}
              placeholder="Enter warehouse or branch"
            />
          ) : null}
          {includeContainerDetails ? (
            <>
              <FormSelect
                label="Seaweeds Container"
                name="sourcePurchaseOrderId"
                value={values.sourcePurchaseOrderId}
                error={errors.sourcePurchaseOrderId}
                onChange={onChange}
                options={containerOptions}
                placeholder="Select completed seaweeds container"
              />
              <ReadOnlyField
                label="Supplier Name"
                value={selectedContainer?.supplierName || '-'}
              />
            </>
          ) : null}
          {includeContainerDetails ? (
            <ReadOnlyField
              label="Sacks Mark"
              value={selectedContainer?.sacksMark || '-'}
            />
          ) : null}
        </div>
      </section>

      <div className="mt-8 flex flex-wrap justify-end gap-3">
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-7 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          type="button"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#d8cec4] disabled:text-[var(--theme-text-muted)] disabled:shadow-none"
          type="submit"
          disabled={isSaving || !canCreate}
        >
          {isSaving ? 'Saving...' : 'Save S.O.'}
        </button>
      </div>
    </form>
  );
}

function FormInput({
  label,
  name,
  value,
  error,
  onChange,
  type = 'text',
  placeholder,
  ...inputProps
}) {
  return (
    <label className="grid w-full min-w-0 self-start gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`min-h-[40px] w-full min-w-0 rounded-full border bg-[var(--theme-surface-warm)] px-3 py-2 text-[0.84rem] outline-none transition placeholder:text-[#b3a79c] ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
        }`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        {...inputProps}
      />
      {error ? <span className="text-[0.82rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function FormSelect({
  label,
  name,
  value,
  error,
  onChange,
  options,
  placeholder,
  disabled = false,
}) {
  return (
    <CustomSelect
      label={label}
      name={name}
      value={value}
      error={error}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div className="grid w-full min-w-0 self-start gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <div className="flex min-h-[40px] w-full min-w-0 items-center rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-3 py-2 text-[0.84rem] text-[#5a514a]">
        {value}
      </div>
    </div>
  );
}

function getOrderDetailsDescription({
  includeCustomerBranch,
  includeContainerDetails,
  includeWarehouseLocation,
}) {
  if (!includeContainerDetails && !includeWarehouseLocation && includeCustomerBranch) {
    return 'Select the customer and delivery location, then set the S.O. date.';
  }

  if (!includeContainerDetails && !includeWarehouseLocation) {
    return 'Select the customer, then set the S.O. date.';
  }

  return 'Select the customer and completed seaweeds container, then fill in the shipping and warehouse details.';
}

function getSalesOrderHeaderDescription({
  includeCustomerBranch,
  includeCommercialFields,
  includeContainerDetails,
}) {
  if (!includeContainerDetails && includeCommercialFields && includeCustomerBranch) {
    return 'Create a customer order with delivery location, seaweed type, FCL quantity, and delivery schedule details.';
  }

  if (!includeContainerDetails && includeCommercialFields) {
    return 'Create a customer order with FCL quantity details.';
  }

  return 'Create a customer order using the oldest available completed seaweeds container from supplier deliveries.';
}

export default SalesOrderForm;
